/*
 * RemoteAccessPanel.js
 * --------------------
 * Provides an interactive SSH shell to edge devices directly from the
 * browser using xterm.js and a WebSocket-to-SSH relay on the server.
 *
 * Protocol:
 *   1. User fills in SSH credentials and clicks Connect.
 *   2. A WebSocket is opened to /node/{nodeId}/ssh via the reverse proxy.
 *   3. Credentials are sent as the first JSON message.
 *   4. The server bridges WebSocket ↔ SSH PTY.
 *   5. xterm.js renders the remote shell in the browser.
 */

import React, { useRef, useEffect, useState, useCallback } from "react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { FiTerminal, FiX, FiLock, FiUser, FiKey } from "react-icons/fi";

// ── Theme matching Weavian dark sidebar ──────────────────────────────────────

const TERM_THEME = {
  background: "#0f172a",
  foreground: "#e2e8f0",
  cursor: "#818cf8",
  cursorAccent: "#0f172a",
  selectionBackground: "rgba(99, 102, 241, 0.35)",
  selectionForeground: "#f8fafc",
  black: "#1e293b",
  red: "#ef4444",
  green: "#22c55e",
  yellow: "#f59e0b",
  blue: "#6366f1",
  magenta: "#a855f7",
  cyan: "#22d3ee",
  white: "#e2e8f0",
  brightBlack: "#475569",
  brightRed: "#f87171",
  brightGreen: "#4ade80",
  brightYellow: "#fbbf24",
  brightBlue: "#818cf8",
  brightMagenta: "#c084fc",
  brightCyan: "#67e8f9",
  brightWhite: "#f8fafc",
};

export default function RemoteAccessPanel({ selectedNode, singleEdge }) {
  const termContainerRef = useRef(null);
  const terminalRef = useRef(null);
  const fitAddonRef = useRef(null);
  const wsRef = useRef(null);
  const inputDisposableRef = useRef(null);

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");

  // SSH form state
  const [username, setUsername] = useState("root");
  const [password, setPassword] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [port, setPort] = useState(22);
  const [authMethod, setAuthMethod] = useState("password");

  // ── Initialize xterm.js ────────────────────────────────────────────────

  useEffect(() => {
    if (!termContainerRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      cursorStyle: "block",
      fontFamily: '"JetBrains Mono", "SF Mono", "Fira Code", "Menlo", monospace',
      fontSize: 14,
      lineHeight: 1.35,
      letterSpacing: 0,
      theme: TERM_THEME,
      allowProposedApi: true,
      scrollback: 5000,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(termContainerRef.current);

    // Small delay to ensure the container has dimensions before fitting
    requestAnimationFrame(() => {
      try { fitAddon.fit(); } catch (_) { /* noop */ }
    });

    terminalRef.current = term;
    fitAddonRef.current = fitAddon;

    term.writeln("\x1b[1;34m╔══════════════════════════════════════════╗");
    term.writeln("║   \x1b[1;37mWeavian Remote Access Terminal\x1b[1;34m       ║");
    term.writeln("╚══════════════════════════════════════════╝\x1b[0m");
    term.writeln("");
    term.writeln(
      "\x1b[90mSelect an edge node and enter credentials to open an SSH session.\x1b[0m"
    );
    term.writeln("");

    // Handle window resize → re-fit terminal
    const handleResize = () => {
      try { fitAddon.fit(); } catch (_) { /* noop */ }
      if (wsRef.current?.readyState === WebSocket.OPEN && term.cols && term.rows) {
        wsRef.current.send(
          JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows })
        );
      }
    };
    window.addEventListener("resize", handleResize);

    // Observe container size changes (e.g. sidebar collapse)
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        try { fitAddon.fit(); } catch (_) { /* noop */ }
      });
      resizeObserver.observe(termContainerRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeObserver) resizeObserver.disconnect();
      term.dispose();
      terminalRef.current = null;
      fitAddonRef.current = null;
    };
  }, []); // once on mount

  // Re-fit when connected state changes (form hides → terminal grows)
  useEffect(() => {
    if (fitAddonRef.current) {
      requestAnimationFrame(() => {
        try { fitAddonRef.current.fit(); } catch (_) { /* noop */ }
      });
    }
  }, [connected]);

  // ── Connect ────────────────────────────────────────────────────────────

  const connect = useCallback(() => {
    if (!selectedNode) return;

    const term = terminalRef.current;
    if (!term) return;

    setConnecting(true);
    setError("");

    term.clear();
    term.writeln(`\x1b[33mConnecting to \x1b[1m${selectedNode}\x1b[0;33m …\x1b[0m`);
    term.writeln("");

    // Build WebSocket URL pointing at the reverse proxy
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = singleEdge
      ? `${wsProtocol}//${window.location.host}/console/api/ssh`
      : `${wsProtocol}//${window.location.host}/node/${encodeURIComponent(
          selectedNode
        )}/ssh`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.binaryType = "arraybuffer";

    ws.onopen = () => {
      const connectMsg = {
        username,
        port,
        cols: term.cols || 80,
        rows: term.rows || 24,
      };
      if (authMethod === "password") {
        connectMsg.password = password;
      } else {
        connectMsg.privateKey = privateKey;
      }
      ws.send(JSON.stringify(connectMsg));
    };

    ws.onmessage = (event) => {
      if (typeof event.data === "string") {
        try {
          const msg = JSON.parse(event.data);
          if (msg.error) {
            setError(msg.error);
            setConnecting(false);
            term.writeln(`\r\n\x1b[31mError: ${msg.error}\x1b[0m`);
            return;
          }
          if (msg.status === "connected") {
            setConnected(true);
            setConnecting(false);
            term.clear();

            // Wire terminal keyboard input → WebSocket
            if (inputDisposableRef.current) {
              inputDisposableRef.current.dispose();
            }
            inputDisposableRef.current = term.onData((data) => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(new TextEncoder().encode(data));
              }
            });

            // Send initial size
            if (term.cols && term.rows) {
              ws.send(
                JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows })
              );
            }
            return;
          }
        } catch (_) {
          // not JSON — treat as text output
          term.write(event.data);
        }
      } else {
        // Binary data from SSH stdout/stderr
        term.write(new Uint8Array(event.data));
      }
    };

    ws.onclose = () => {
      setConnected(false);
      setConnecting(false);
      if (inputDisposableRef.current) {
        inputDisposableRef.current.dispose();
        inputDisposableRef.current = null;
      }
      term.writeln("\r\n\x1b[90m── Session closed ──\x1b[0m");
    };

    ws.onerror = () => {
      setError("WebSocket connection failed");
      setConnecting(false);
      term.writeln("\r\n\x1b[31mWebSocket connection failed.\x1b[0m");
    };
  }, [selectedNode, username, password, privateKey, port, authMethod]);

  // ── Disconnect ─────────────────────────────────────────────────────────

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (inputDisposableRef.current) {
      inputDisposableRef.current.dispose();
      inputDisposableRef.current = null;
    }
    setConnected(false);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (inputDisposableRef.current) {
        inputDisposableRef.current.dispose();
      }
    };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────

  const noNode = !selectedNode;

  return (
    <div className="remote-access-root">
      {/* ── Connection toolbar ── */}
      {!connected && (
        <div className="panel ssh-connect-panel">
          <div className="panel-title">
            <FiLock size={14} style={{ marginRight: 6 }} />
            SSH Connection
          </div>
          <div className="panel-sub">
            Enter credentials to open a secure shell on the selected edge node.
          </div>

          <div className="ssh-form-grid">
            <label className="ssh-label">
              <FiUser size={13} />
              <span>Username</span>
            </label>
            <input
              className="ssh-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="root"
              disabled={connecting}
            />

            <label className="ssh-label">
              <FiKey size={13} />
              <span>Auth Method</span>
            </label>
            <select
              className="ssh-input"
              value={authMethod}
              onChange={(e) => setAuthMethod(e.target.value)}
              disabled={connecting}
            >
              <option value="password">Password</option>
              <option value="key">Private Key (PEM)</option>
            </select>

            {authMethod === "password" ? (
              <>
                <label className="ssh-label">
                  <FiLock size={13} />
                  <span>Password</span>
                </label>
                <input
                  className="ssh-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={connecting}
                />
              </>
            ) : (
              <>
                <label className="ssh-label" style={{ alignSelf: "start", marginTop: 4 }}>
                  <FiKey size={13} />
                  <span>Private Key</span>
                </label>
                <textarea
                  className="ssh-input ssh-textarea"
                  value={privateKey}
                  onChange={(e) => setPrivateKey(e.target.value)}
                  placeholder={"-----BEGIN OPENSSH PRIVATE KEY-----\n..."}
                  rows={4}
                  disabled={connecting}
                />
              </>
            )}

            <label className="ssh-label">
              <FiTerminal size={13} />
              <span>Port</span>
            </label>
            <input
              className="ssh-input ssh-port"
              type="number"
              min={1}
              max={65535}
              value={port}
              onChange={(e) => setPort(parseInt(e.target.value, 10) || 22)}
              disabled={connecting}
            />
          </div>

          {error && <div className="panel-error">{error}</div>}

          <div className="ssh-actions">
            <button
              className="apply-btn ssh-connect-btn"
              onClick={connect}
              disabled={connecting || noNode}
            >
              {connecting
                ? "Connecting…"
                : noNode
                ? "Select an edge node first"
                : `Connect to ${selectedNode}`}
            </button>
          </div>
        </div>
      )}

      {/* ── Disconnect bar ── */}
      {connected && (
        <div className="ssh-toolbar">
          <div className="ssh-toolbar-info">
            <span className="ssh-status-dot online" />
            <span>
              Connected to <strong>{selectedNode}</strong> as{" "}
              <code>{username}</code>
            </span>
          </div>
          <button className="danger-btn ssh-disconnect-btn" onClick={disconnect}>
            <FiX size={14} />
            <span>Disconnect</span>
          </button>
        </div>
      )}

      {/* ── Terminal viewport ── */}
      <div
        className={`ssh-terminal-container ${connected ? "ssh-terminal-active" : ""}`}
        ref={termContainerRef}
      />
    </div>
  );
}
